import React, { useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useGetAppointmentQuery, useUpdateAppointmentMutation } from "store";
import {
    Box,
    Spinner,
    SimpleGrid,
    Card,
    CardBody,
    CardHeader,
    FormControl,
    FormLabel,
    FormErrorMessage,
    Input,
    Select,
    Button,
    Text,
    Textarea,
    Grid,
    GridItem,
    Link as ChakraLink,
} from "@chakra-ui/react";

const AppointmentUpdate = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    // Fetch the existing prescription data
    const { data: appointment, error, isLoading } = useGetAppointmentQuery(id);

    // Mutation hook for updating the prescription
    const [
        updateAppointment,
        { isLoading: isUpdating, error: updateError },
    ] = useUpdateAppointmentMutation();

    // Initialize the form with useForm
    const {
        register,
        handleSubmit,
        setValue,
        reset,
        formState: { errors },
    } = useForm();

    const capitalizeFirstLetter = (str) => {
        if (!str) return str; // Handle empty string case
        return str.charAt(0).toUpperCase() + str.slice(1);
    };

    // Populate the form with existing prescription data
    useEffect(() => {
        if (appointment) {
            reset({
                name: appointment.name,
                phone: appointment.phone,
                date: appointment.date,
                time: appointment.time,
                address: appointment.address,
            });
        }
    }, [appointment, reset]);

    // Handle form submission
    const onSubmit = async (data) => {
        try {
            await updateAppointment({
                id,
                ...data,
            }).unwrap();
            navigate(`/appointments/${id}/details`);
        } catch (error) {
            console.error("Error updating appointment:", error);
        }
    };

    if (isLoading) return <div>Loading...</div>;
    if (error) return <div>Error: {error.message}</div>;

    return (
        <Box mt={20} p={4}>
            <Card
                boxShadow="md"
                borderRadius="2xl"
                overflow="hidden"
                p={4}
                border="1px solid"
                borderColor="gray.300"
            >
                <CardHeader borderBottom="1px solid" borderColor="blue.200">
                    <Box textAlign="center" mb={4}>
                        <Text fontSize="2xl" fontWeight="bold">
                            Update Appointment
                        </Text>
                    </Box>
                </CardHeader>

                <CardBody>
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <SimpleGrid columns={{ base: 1, md: 2 }} mb={6} spacing={6}>
                            {/* Name */}
                            <Box>
                                <FormControl isInvalid={errors.name}>
                                    <FormLabel>Name</FormLabel>
                                    <Input
                                        {...register("name", {
                                            onChange: (e) => {
                                                const capitalizedValue = capitalizeFirstLetter(
                                                    e.target.value
                                                );
                                                setValue(
                                                    "name",
                                                    capitalizedValue,
                                                    { shouldValidate: true }
                                                );
                                            },
                                        })}
                                    />
                                    <FormErrorMessage>
                                        {errors.name && errors.name.message}
                                    </FormErrorMessage>
                                </FormControl>
                            </Box>

                            {/* Telephone Number */}
                            <Box>
                                <FormControl isInvalid={errors.phone}>
                                    <FormLabel>Telephone Number</FormLabel>
                                    <Input
                                        type="tel"
                                        {...register("phone", {
                                            valueAsNumber: true,
                                        })}
                                    />
                                    {errors.phone && (
                                        <FormErrorMessage>
                                            Telephone number is required
                                        </FormErrorMessage>
                                    )}
                                </FormControl>
                            </Box>
                        </SimpleGrid>

                        {/* Row 2 */}
                        <SimpleGrid
                            columns={{ base: 1, md: 2 }}
                            spacing={4}
                            mb={4}
                        >
                            {/* Dentist Choice */}
                            <Box>
                                <FormControl>
                                    <FormLabel>Choice of Dentist</FormLabel>
                                    <Select {...register("dent_choice")}>
                                        <option value="ROSE">
                                            Dr. Naale Rose
                                        </option>
                                        <option value="ANDREW">
                                            Mr. Andrew Williams
                                        </option>
                                        <option value="NONE">
                                            No Preference
                                        </option>
                                    </Select>
                                </FormControl>
                            </Box>
                                
                                {/* Address */}
                            <Box>
                                <FormControl isInvalid={errors.address}>
                                    <FormLabel>Address</FormLabel>
                                    <Input
                                        {...register("address", {
                                            onChange: (e) => {
                                                const capitalizedValue = capitalizeFirstLetter(
                                                    e.target.value
                                                );
                                                setValue(
                                                    "address",
                                                    capitalizedValue,
                                                    { shouldValidate: true }
                                                );
                                            },
                                        })}
                                    />
                                    <FormErrorMessage>
                                        {errors.address && errors.address.message}
                                    </FormErrorMessage>
                                </FormControl>
                            </Box>

                            {/* Service Choice */}
                            <Box>
                                <FormControl>
                                    <FormLabel>Service</FormLabel>
                                    <Select {...register("service_choice")}>
                                        <option value="chk_up">Check Up</option>
                                        <option value="fill">Filling</option>
                                        <option value="ext">Extraction</option>
                                        <option value="clean">Cleaning</option>
                                        <option value="rct">
                                            Root Canal Therapy
                                        </option>
                                        <option value="other">Other</option>
                                    </Select>
                                </FormControl>
                            </Box>
                        </SimpleGrid>

                        {/* Row 3 */}
                        <SimpleGrid
                            columns={{ base: 1, md: 2 }}
                            spacing={6}
                            mb={6}
                        >
                            {/* Date of Appointment */}
                            <Box>
                                <FormControl isInvalid={errors.date}>
                                    <FormLabel>Date</FormLabel>
                                    <Input
                                        type="date"
                                        {...register("date", {
                                            required: true,
                                        })}
                                    />
                                    {errors.date && (
                                        <FormErrorMessage>
                                            Date of Appointment is required
                                        </FormErrorMessage>
                                    )}
                                </FormControl>
                            </Box>

                            {/* Time of Appointment */}
                            <Box>
                                <FormControl isInvalid={errors.time}>
                                    <FormLabel>Time</FormLabel>
                                    <Input
                                        type="time"
                                        {...register("time", {
                                            required: true,
                                        })}
                                    />
                                    {errors.time && (
                                        <FormErrorMessage>
                                            Time of Appointment is required
                                        </FormErrorMessage>
                                    )}
                                </FormControl>
                            </Box>
                        </SimpleGrid>

                        <Grid
                            templateColumns="repeat(2, 1fr)"
                            gap={4}
                            width="100%"
                        >
                            <GridItem>
                                <ChakraLink
                                    as={Link}
                                    to={`/appointments/${id}/details`}
                                    color="blue.500"
                                >
                                    Back to Details
                                </ChakraLink>
                            </GridItem>
                            <GridItem textAlign="right">
                                <Button
                                    colorScheme="blue"
                                    isLoading={isUpdating}
                                    loadingText="Loading..."
                                    spinner={<Spinner />}
                                    type="submit"
                                >
                                    Update
                                </Button>
                            </GridItem>
                        </Grid>

                        {updateError && <div>Error: {updateError.message}</div>}
                    </form>
                </CardBody>
            </Card>
        </Box>
    );
};

export default AppointmentUpdate;
